import React from 'react'
import { TaskRow, Checkbox, DueDate, TaskNameCell } from './styles'
import useModals from '@/v2/hooks/useModals'
import { useUpdateTask } from '@/v2/hooks/useTasks'
import { useClaims } from '@/v2/hooks/insurance/useClaims'
import { format } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import { Button } from '@mui/material'
import { useNavigate } from 'react-router-dom'

const TaskRowComponent = ({ task }) => {
  const { goTo } = useModals()
  const { updateTask } = useUpdateTask(task.id)
  const navigate = useNavigate()
  const { claims = [] } = useClaims(task.patient?.id)
  const handleTaskCheck = async isChecked => {
    try {
      await updateTask({
        completed: isChecked,
      })
    } catch (error) {
      console.error('Error updating task:', error)
    }
  }

  const handleTaskClick = task => {
    goTo('/v2/task/new', { task })
  }

  const handleLinkableClick = () => {
    const patientId = task.linkable?.patientId || task.patient?.id

    if (!patientId) return

    if (task.linkable?.type === 'Insurance::Claim') {
      const claimId = task.linkable.id
      const claim = claims.find(c => c.id === claimId)

      if (claim) {
        navigate(`/v2/patient/${patientId}/insurance`, {
          state: {
            openTab: 'claims',
            openClaimId: claimId,
            claim: claim,
            patient: task.patient,
            scrollToHistory: true,
          },
        })
      } else {
        navigate(`/v2/patient/${patientId}/insurance`, {
          state: {
            openTab: 'claims',
            openClaimId: claimId,
            scrollToHistory: true,
          },
        })
      }
    } else {
      const url = `/v2/patient/${patientId}/profile#patient-insurance`
      window.location.href = url
    }
  }

  const formatDueDate = date => {
    if (!date) return ''
    const today = new Date()
    const dueDate = new Date(date)

    // Check if due today
    if (format(dueDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
      return `Today (${format(dueDate, 'MMM. dd')})`
    }

    // Check if overdue
    if (dueDate < today) {
      const diffDays = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24))
      return `${diffDays} Days Ago (${format(dueDate, 'MMM. dd')})`
    }

    // Check if due in next 2 days
    const twoDaysFromNow = new Date(today)
    twoDaysFromNow.setDate(today.getDate() + 2)
    if (dueDate <= twoDaysFromNow) {
      return 'In 2 Days'
    }

    // Default format for other dates
    return format(dueDate, 'MMM dd, yyyy')
  }

  return (
    <TaskRow key={task.id} $completed={task.completed}>
      <div>
        <Checkbox
          checked={task.completed}
          onChange={e => handleTaskCheck(e.target.checked)}
        />
      </div>
      <TaskNameCell onClick={() => handleTaskClick(task)}>
        {task.title}
      </TaskNameCell>
      <div>{safeFormat(new Date(task.createdAt), 'dd MMM, yyyy')}</div>
      <DueDate $isOverdue={new Date(task.dueDate) < new Date()}>
        {formatDueDate(task.dueDate)}
      </DueDate>
      <div>{task.patient?.fullName || 'Internal'}</div>
      <div>
        {task.assignedBy?.fullName || task.assignedBy?.email || 'Unassigned'}
      </div>
      <div style={{ whiteSpace: 'nowrap', minWidth: '100px' }}>
        {task.linkable && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleLinkableClick}
            style={{ minWidth: 'auto' }}
          >
            {task.linkable.type === 'InsuranceForm'
              ? 'Review Insurance'
              : task.linkable.type === 'Insurance::Claim'
              ? 'Review Claim'
              : 'Internal'}
          </Button>
        )}
      </div>
    </TaskRow>
  )
}

export default TaskRowComponent
